import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react'
import type { JSONContent } from '@tiptap/core'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

import { cn } from '@/lib/utils'
import {
  getEditorPlainText,
  type RichTextVariableToken,
  updateVariableTokenDecorations,
  variableTokenDecorations,
} from '@/hooks/use-rich-text-editor'
import { getTemplateVariableGroupConfig } from '@/lib/template-variable-groups'
import {
  createTemplateVariableSplitter,
  matchTemplateVariableToken,
  toTemplateVariableSyntax,
} from '@/lib/template-variable-tokens'

export interface TemplateVariableTextEditorHandle {
  focus: () => void
  insertText: (text: string) => void
}

interface TemplateVariableTextEditorProps {
  id?: string
  value: string
  onChange: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  placeholder?: string
  variableTokens: RichTextVariableToken[]
  singleLine?: boolean
  className?: string
  editorClassName?: string
}

function plainTextToTiptapContent(value: string): JSONContent {
  const lines = value.split('\n')

  return {
    type: 'doc',
    content: lines.map((line) => ({
      type: 'paragraph',
      content: line ? [{ type: 'text', text: line }] : undefined,
    })),
  }
}

function renderTemplateVariableText(text: string, variableTokens: RichTextVariableToken[]) {
  const tokensBySyntax = new Map(variableTokens.map((token) => [token.syntax, token]))
  const parts = text
    .split(createTemplateVariableSplitter())
    .filter((part) => part !== undefined && part !== '')

  return parts.map((part, index) => {
    const name = matchTemplateVariableToken(part)
    if (!name) return part

    const normalizedSyntax = toTemplateVariableSyntax(name)
    const token = tokensBySyntax.get(part) ?? tokensBySyntax.get(normalizedSyntax)
    const config = getTemplateVariableGroupConfig(token?.parentType ?? 'General')

    return (
      <span
        key={`${part}-${index}`}
        className={cn(
          'mx-0.5 inline-flex items-center rounded-full border px-1.5 py-0.5 text-xs font-medium leading-none',
          token ? config.color : 'border-status-danger-border bg-status-danger-bg text-status-danger-fg'
        )}
      >
        {token?.label ?? normalizedSyntax}
      </span>
    )
  })
}

export const TemplateVariableTextEditor = forwardRef<
  TemplateVariableTextEditorHandle,
  TemplateVariableTextEditorProps
>(function TemplateVariableTextEditor(
  {
    id,
    value,
    onChange,
    onFocus,
    onBlur,
    placeholder,
    variableTokens,
    singleLine = false,
    className,
    editorClassName,
  },
  ref
) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      variableTokenDecorations.configure({
        highlightUnresolvedTemplateVariables: true,
        variableTokens,
      }),
    ],
    content: plainTextToTiptapContent(value),
    editorProps: {
      attributes: {
        ...(id ? { id } : {}),
        'aria-multiline': String(!singleLine),
        class: cn(
          'relative z-10 w-full bg-transparent px-3 py-2 text-base outline-none md:text-sm',
          singleLine
            ? 'min-h-9 overflow-x-auto overflow-y-hidden whitespace-nowrap'
            : 'min-h-20 whitespace-pre-wrap break-words',
          editorClassName
        ),
      },
      handleKeyDown: (_view, event) => {
        if (!singleLine || event.key !== 'Enter') return false

        event.preventDefault()
        return true
      },
      handlePaste: (view, event) => {
        if (!singleLine) return false

        const text = event.clipboardData?.getData('text/plain')
        if (text === undefined) return false

        event.preventDefault()
        view.dispatch(view.state.tr.insertText(text.replace(/[\r\n]+/g, ' ')))
        return true
      },
    },
    onUpdate: ({ editor }) => {
      onChange(getEditorPlainText(editor))
    },
    onFocus: () => {
      onFocus?.()
    },
    onBlur: () => {
      onBlur?.()
    },
  })

  useEffect(() => {
    if (!editor) return

    updateVariableTokenDecorations(editor, {
      highlightUnresolvedTemplateVariables: true,
      variableTokens,
    })
  }, [editor, variableTokens])

  useEffect(() => {
    if (!editor) return

    const currentValue = getEditorPlainText(editor)
    if (currentValue === value) return

    editor.commands.setContent(plainTextToTiptapContent(value))
  }, [editor, value])

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editor?.commands.focus()
      },
      insertText: (text: string) => {
        editor?.chain().focus().insertContent(text).run()
      },
    }),
    [editor]
  )

  const renderedPlaceholder = useMemo(
    () => (placeholder ? renderTemplateVariableText(placeholder, variableTokens) : null),
    [placeholder, variableTokens]
  )

  return (
    <div
      className={cn(
        'border-input focus-within:border-ring focus-within:ring-ring/50 dark:bg-input/30 relative w-full rounded-md border bg-transparent shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]',
        className
      )}
    >
      {!value && renderedPlaceholder ? (
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 top-0 z-0 px-3 py-2 text-base text-muted-foreground md:text-sm',
            singleLine ? 'truncate whitespace-nowrap' : 'whitespace-pre-wrap break-words'
          )}
        >
          {renderedPlaceholder}
        </div>
      ) : null}
      <EditorContent editor={editor} />
    </div>
  )
})
